import { Select, type SelectProps, type GetRef } from 'antd'
import {
  forwardRef,
  useEffect,
  useMemo,
  useState,
  type ForwardRefRenderFunction
} from 'react'
import { AWS_REGIONS } from '~/utils/constants'

type DefaultOptionType = NonNullable<SelectProps['options']>[number]
type BaseSelectRef = GetRef<typeof Select>

interface RegionSelectProps {
  withNonOption?: boolean
  showRegionCode?: boolean
  rememberSelection?: boolean
}

const RegionSelectFwdRef: ForwardRefRenderFunction<
  BaseSelectRef,
RegionSelectProps & SelectProps
> = (
  {
    withNonOption = false,
    showRegionCode = false,
    rememberSelection = true,
    ...props
  },
  ref
) => {
  const [selectedOption, setSelectedOption] = useState(() =>
    rememberSelection ? localStorage.getItem('last_selected_region') : null
  )
  const options = useMemo(() => {
    const items: DefaultOptionType[] = AWS_REGIONS.map((item) => ({
      value: item.code,
      label: showRegionCode ? `${item.name} - ${item.code}` : item.name
    }))

    if (withNonOption) {
      items.push({
        value: null,
        label: 'Auto'
      })
    }

    return items
  }, [showRegionCode, withNonOption])

  useEffect(() => {
    if (
      rememberSelection &&
      selectedOption != null &&
      props.onChange != null
    ) {
      const option = options?.find((i) => i.value === selectedOption)

      if (option != null) {
        props.onChange(selectedOption, option)
      }
    }
  }, [options, props, rememberSelection, selectedOption])

  return (
    <Select
      showSearch
      optionFilterProp='label'
      options={options}
      defaultValue={selectedOption}
      ref={ref}
      onSelect={(value, options) => {
        if (rememberSelection) {
          setSelectedOption(value)
          localStorage.setItem('last_selected_region', value)
        }
        if (props.onSelect != null) props.onSelect(value, options)
      }}
      {...props}
    />
  )
}

export const RegionSelect = forwardRef(RegionSelectFwdRef)
